import React, { useState, useEffect, useCallback } from 'react';
import { demoRequestsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowsClockwise, Trash, EnvelopeSimple, Phone, Buildings, User,
  Clock, Sparkle, CheckCircle, Star,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../lib/utils';

const STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'won', 'lost'];
const STATUS_STYLES = {
  new:        { bg: 'bg-[#41B6E6]/15', text: 'text-[#1E7CA6]', icon: Sparkle },
  contacted:  { bg: 'bg-[#FFD100]/25', text: 'text-[#B5940A]', icon: Clock },
  qualified:  { bg: 'bg-[#8347AD]/15', text: 'text-[#5B2E91]', icon: Star },
  won:        { bg: 'bg-[#00B140]/15', text: 'text-[#00813A]', icon: CheckCircle },
  lost:       { bg: 'bg-[#E4002B]/15', text: 'text-[#B4001F]', icon: Trash },
};

export default function DemoRequestsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const isAdmin = user && user.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await demoRequestsApi.list(filter === 'all' ? null : filter);
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load demo requests');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const updateStatus = async (id, status) => {
    try {
      await demoRequestsApi.update(id, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this demo request?')) return; // eslint-disable-line no-alert
    try {
      await demoRequestsApi.delete(id);
      toast.success('Demo request deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-10" data-testid="demo-requests-admin-only">
        <div className="bg-card border border-border rounded-sm p-8 text-center">
          <p className="font-heading text-lg font-semibold">Admin access required</p>
          <p className="text-sm text-muted-foreground mt-1">You must be an admin to view demo requests.</p>
        </div>
      </div>
    );
  }

  const stats = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = items.filter((i) => i.status === s).length;
    return acc;
  }, {});

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="demo-requests-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Admin</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Demo Requests</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Leads submitted from the public pricing page. Triage and move them through the sales funnel.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          className="rounded-sm"
          data-testid="refresh-btn"
        >
          <ArrowsClockwise size={16} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Status summary + filter */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="status-summary">
        {STATUS_OPTIONS.map((s) => {
          const style = STATUS_STYLES[s];
          const Icon = style.icon;
          const isActive = filter === s;
          return (
            <button
              key={s}
              onClick={() => setFilter(isActive ? 'all' : s)}
              className={`bg-card border rounded-sm p-4 text-left transition-all ${
                isActive ? 'border-[#002677] shadow-md' : 'border-border hover:border-foreground/30'
              }`}
              data-testid={`filter-${s}`}
            >
              <div className="flex items-center justify-between">
                <div className={`h-8 w-8 rounded-sm ${style.bg} flex items-center justify-center`}>
                  <Icon size={16} weight="duotone" className={style.text} />
                </div>
                <span className="font-heading text-2xl font-semibold">{stats[s] || 0}</span>
              </div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mt-2">
                {s}
              </p>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="font-medium">No demo requests yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Requests submitted from /pricing will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="demo-requests-table">
              <thead className="bg-muted/30 border-b border-border">
                <tr>
                  <th className="text-left font-semibold px-5 py-3">Contact</th>
                  <th className="text-left font-semibold px-5 py-3">Company</th>
                  <th className="text-left font-semibold px-5 py-3">Plan</th>
                  <th className="text-left font-semibold px-5 py-3">Size</th>
                  <th className="text-left font-semibold px-5 py-3">Message</th>
                  <th className="text-left font-semibold px-5 py-3">Submitted</th>
                  <th className="text-left font-semibold px-5 py-3">Status</th>
                  <th className="text-right font-semibold px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const style = STATUS_STYLES[r.status] || STATUS_STYLES.new;
                  return (
                    <tr
                      key={r.id}
                      className="border-b border-border/50 last:border-b-0 hover:bg-accent/20"
                      data-testid={`demo-row-${r.id}`}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-muted-foreground" />
                          <span className="font-medium">{r.full_name}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-foreground">
                            <EnvelopeSimple size={12} /> {r.email}
                          </a>
                          {r.phone && (
                            <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-foreground">
                              <Phone size={12} /> {r.phone}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        {r.company ? (
                          <span className="flex items-center gap-1">
                            <Buildings size={12} className="text-muted-foreground" />
                            {r.company}
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        {r.plan ? <Badge variant="outline" className="capitalize">{r.plan}</Badge> : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-5 py-3 text-muted-foreground">{r.company_size || '—'}</td>
                      <td className="px-5 py-3 max-w-xs">
                        <p className="text-muted-foreground truncate" title={r.message}>
                          {r.message || '—'}
                        </p>
                      </td>
                      <td className="px-5 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(r.submitted_at)}
                      </td>
                      <td className="px-5 py-3">
                        <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                          <SelectTrigger className={`rounded-sm h-8 text-xs w-[140px] ${style.bg} border-0 ${style.text} font-medium`} data-testid={`status-select-${r.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(r.id)}
                          className="text-destructive hover:bg-destructive/10"
                          data-testid={`delete-${r.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
